import { CollapsiblePanel, FormDirty, FormErrorDisplayMode, FormErrors, FormFields, FormFocused, FormInitialValue, FormLabels, FormLabelsContext, FormLookups, FormLookupsContext, FormOptionsContext, FormState, FormTouched, FormValue, IFormLabel, ILookup, IValidationError, WindowPortal } from "@ngt/forms";
import { useCallback, useMemo, useState } from "react";
import { IFormProps as ICoreFormProps} from '@ngt/forms-core';
import MySuperAwesomeForm from "./MySuperAwesomeForm";
import { Box, Button } from "@mui/material";

export interface IFormProps<TFormValue extends object> extends ICoreFormProps<TFormValue, IValidationError> {
    lookups?: Array<Partial<ILookup>>;
    labels?: IFormLabel[];
    errorMode?: FormErrorDisplayMode;
    readOnly?: boolean;
    debug?: boolean;
    debugLocation?: 'self' | 'portal';
}

const defaultLookups: Array<Partial<ILookup>> = [];
const defaultLabels: IFormLabel[] = [];
const defaultValue = {};

const StandaloneForm = <TFormValue extends object>({
    lookups,
    labels,
    errorMode,
    readOnly,
    debug,
    debugLocation,
    children,
    initialValue,
    ...coreFormProps
}: IFormProps<TFormValue>) => {
    const formOptions = useMemo(() => {
        return {
            errorMode: errorMode !== undefined ? errorMode : FormErrorDisplayMode.AutoShowAllErrors,
            readOnly: readOnly === true
        }
    }, [errorMode, readOnly]);
    
    const [debugOpen, setDebugOpen] = useState(false);

    const onClose = useCallback(() => {
        setDebugOpen(false);
    }, [setDebugOpen])

    return (
        <FormOptionsContext.Provider value={formOptions}>
            <FormLookupsContext.Provider value={lookups ?? defaultLookups}>
                <FormLabelsContext.Provider value={labels ?? defaultLabels}>
                    <MySuperAwesomeForm
                        {...coreFormProps}
                        initialValue={initialValue ?? defaultValue as TFormValue}
                    >
                        {/* <FormErrorHandler> */}
                            {children}{
                                debug && debugLocation === 'self' && (
                                    <CollapsiblePanel 
                                        ComponentProps={{
                                            title: 'Debug'
                                        }}
                                    >
                                        <Box sx={{pl: 4}}>
                                            <FormState />
                                            <FormFields />
                                            <FormInitialValue />
                                            <FormValue />
                                            <FormTouched />
                                            <FormDirty />
                                            <FormFocused />
                                            <FormErrors />
                                            <FormLabels />
                                            <FormLookups />
                                        </Box>
                                    </CollapsiblePanel>
                                )
                            }       
                            {
                                debug && debugLocation !== 'self' && (
                                    <>
                                        <Button 
                                            fullWidth 
                                            variant="contained"
                                            color="secondary"
                                            onClick={() => setDebugOpen(c => !c)}
                                            sx={{
                                                mt: 2
                                            }}
                                        >
                                            Debug
                                        </Button>
                                        <WindowPortal
                                            onClose={onClose}
                                            open={debugOpen}
                                        >
                                            <Box>
                                                <FormState />
                                                <FormFields />
                                                <FormInitialValue />
                                                <FormValue />
                                                <FormTouched />
                                                <FormDirty />
                                                <FormFocused />
                                                <FormErrors />
                                                <FormLabels />
                                                <FormLookups />
                                            </Box>
                                        </WindowPortal>
                                    </>
                                )
                            }                            
                        {/* </FormErrorHandler> */}
                    </MySuperAwesomeForm>
                </FormLabelsContext.Provider>
            </FormLookupsContext.Provider>
        </FormOptionsContext.Provider>
    )
};

export default StandaloneForm;